import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Observable, ReplaySubject, timer } from 'rxjs';
import { finalize, share, takeUntil } from 'rxjs/operators';

/**
 * HTTP Interceptor that prevents duplicate requests to the same endpoint
 *
 * This interceptor implements request deduplication by:
 * 1. Creating a unique cache key for each request based on method, URL, and body
 * 2. Storing ongoing requests in a cache using ReplaySubject
 * 3. Sharing the same observable for identical concurrent requests
 * 4. Automatically cleaning up completed requests from cache
 * 5. Adding timeout protection to prevent memory leaks
 *
 * Benefits:
 * - Reduces server load by preventing duplicate requests
 * - Improves performance by sharing responses
 * - Maintains data consistency across components
 * - Prevents race conditions from simultaneous identical requests
 */

const pendingRequests = new Map<string, ReplaySubject<HttpEvent<unknown>>>();
const REQUEST_TIMEOUT = 3000; // 3 seconds timeout
const CACHE_CLEANUP_DELAY = 1000; // 1 second delay before cache cleanup

/**
 * Determines whether a request should be deduplicated
 * Currently only GET requests are deduplicated as they are idempotent
 */
function shouldDeduplicateRequest(request: HttpRequest<unknown>): boolean {
  // Only deduplicate GET requests (safe, idempotent operations)
  if (request.method !== 'GET') {
    return false;
  }

  // Skip deduplication for requests with specific headers that indicate they should not be cached
  const noCacheHeaders = ['cache-control', 'pragma'];
  for (const header of noCacheHeaders) {
    const headerValue = request.headers.get(header);
    if (headerValue && (headerValue.includes('no-cache') || headerValue.includes('no-store'))) {
      return false;
    }
  }

  return true;
}

/**
 * Generates a unique cache key for the request
 * Includes method, URL with parameters, and body content
 */
function generateCacheKey(request: HttpRequest<unknown>): string {
  const method = request.method;
  const urlWithParams = request.urlWithParams;
  const body = request.body ? JSON.stringify(request.body) : '';

  // Include relevant headers that might affect the response
  const relevantHeaders: string[] = [];
  const headersToInclude = ['authorization', 'accept', 'content-type'];

  headersToInclude.forEach((headerName) => {
    const headerValue = request.headers.get(headerName);
    if (headerValue) {
      relevantHeaders.push(`${headerName}:${headerValue}`);
    }
  });

  const headersString = relevantHeaders.join(';');

  return `${method}:${urlWithParams}:${body}:${headersString}`;
}

export const duplicateRequestInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  // Only deduplicate GET requests by default (safe operations)
  // You can extend this to include other idempotent operations if needed
  if (!shouldDeduplicateRequest(request)) {
    return next(request);
  }

  const cacheKey = generateCacheKey(request);

  // Check if an identical request is already in progress
  if (pendingRequests.has(cacheKey)) {
    const existingRequest = pendingRequests.get(cacheKey)!;
    return existingRequest.asObservable();
  }

  // Create a new ReplaySubject to store and share the response
  const subject = new ReplaySubject<HttpEvent<unknown>>(1);
  pendingRequests.set(cacheKey, subject);

  // Create timeout observable to prevent memory leaks
  const timeout$ = timer(REQUEST_TIMEOUT);

  // Execute the actual HTTP request
  const request$ = next(request).pipe(
    takeUntil(timeout$), // Cancel request if it takes too long
    share(), // Share the observable among multiple subscribers
    finalize(() => {
      // Clean up the cache after a short delay to allow concurrent requests to complete
      setTimeout(() => {
        pendingRequests.delete(cacheKey);
        subject.complete();
      }, CACHE_CLEANUP_DELAY);
    }),
  );

  // Subscribe to the request and forward all events to the ReplaySubject
  request$.subscribe({
    next: (event) => subject.next(event),
    error: (error) => {
      subject.error(error);
      // Immediately remove failed requests from cache
      pendingRequests.delete(cacheKey);
    },
    complete: () => {
      // Note: We don't complete the subject here because finalize will handle cleanup
    },
  });

  return subject.asObservable();
};

/**
 * Manually clear all pending requests (useful for testing or force refresh)
 */
export function clearDuplicateRequestCache(): void {
  pendingRequests.forEach((subject) => {
    subject.complete();
  });
  pendingRequests.clear();
}

/**
 * Get the number of currently pending requests (useful for debugging)
 */
export function getPendingRequestsCount(): number {
  return pendingRequests.size;
}
